/**
 * Trainova AI — Type-Safe Frontend API Client
 *
 * Adapted from apps/web/src/services/api.ts
 *
 * Changes from the reference implementation:
 *  - Token key standardized to `trainova_access_token` (existing production key)
 *  - Base URL reads from VITE_API_BASE_URL env var, defaults to Express port 3001
 *  - Request timeout added
 *  - Centralized error handling improved
 *
 * All API requests MUST go through this client.
 * Do NOT scatter fetch() calls through UI components.
 */

export const PROD_API_ORIGIN = 'https://trainova.co.in';

export function getApiBaseUrl(): string {
  // 1. Explicit window runtime override if injected
  const customWindowUrl = typeof window !== 'undefined' ? (window as any).TRAINOVA_API_BASE_URL : undefined;
  if (customWindowUrl && typeof customWindowUrl === 'string' && customWindowUrl.trim() !== '') {
    return customWindowUrl.trim().replace(/\/$/, '');
  }

  // 2. Vite environment variable
  const rawBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (rawBaseUrl && rawBaseUrl.trim() !== '') {
    return rawBaseUrl.trim().replace(/\/$/, '');
  }

  // 3. Local Vite dev server with proxy
  if (import.meta.env.DEV) {
    return '';
  }

  // 4. Localhost preview / node environments
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return (window.location.port === '3000' || window.location.port === '5173' || window.location.port === '4173') ? 'http://localhost:3001' : '';
    }
    // 5. Render deployment (Node Express serves both SPA frontend and API on same origin)
    if (window.location.hostname.endsWith('.onrender.com')) {
      return '';
    }
    // 6. Default in production (including on *.vercel.app) to authoritative backend origin
    return PROD_API_ORIGIN;
  }

  return PROD_API_ORIGIN;
}

export const API_BASE_URL = getApiBaseUrl();

/** The authoritative token keys used by the system */
const TOKEN_KEY = 'trainova_access_token';
const REFRESH_TOKEN_KEY = 'trainova_refresh_token';

const REQUEST_TIMEOUT_MS = 120_000;

// Mutex / promise for single in-flight token refresh
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const activeBaseUrl = getApiBaseUrl();
      const res = await fetch(`${activeBaseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        clearStoredTokens();
        return null;
      }

      const text = await res.text();
      let json: any = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        json = null;
      }
      if (json && json.success && json.data?.accessToken) {
        const newAccessToken = json.data.accessToken;
        setStoredToken(newAccessToken);
        if (json.data.refreshToken) {
          setStoredRefreshToken(json.data.refreshToken);
        }
        return newAccessToken;
      }
      clearStoredTokens();
      return null;
    } catch {
      clearStoredTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  message?: string;
  requestId?: string;
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly requestId?: string;

  constructor(code: string, message: string, statusCode: number, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.requestId = requestId;
  }
}

// ---------------------------------------------------------------------------
// In-flight GET request deduplication cache to prevent concurrent duplicate network requests
const inFlightRequests = new Map<string, Promise<ApiResponse<any>>>();

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

async function apiRequest<T = unknown>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
  body?: unknown,
  params?: Record<string, string | number | boolean>,
  isRetry = false,
  forcedBaseUrl?: string
): Promise<ApiResponse<T>> {
  const currentBaseUrl = forcedBaseUrl !== undefined ? forcedBaseUrl : getApiBaseUrl();
  let url = (path.startsWith('http://') || path.startsWith('https://'))
    ? path
    : `${currentBaseUrl}${path}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        searchParams.append(key, String(val));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) url += `?${queryString}`;
  }

  const token = getStoredToken();

  // Deduplicate concurrent in-flight GET requests
  const dedupeKey = method === 'GET' ? `${url}|${token || ''}` : null;
  if (dedupeKey && !isRetry) {
    const existingPromise = inFlightRequests.get(dedupeKey);
    if (existingPromise) {
      return existingPromise as Promise<ApiResponse<T>>;
    }
  }

  const executeRequest = async (): Promise<ApiResponse<T>> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // If we receive a 502/503/504 (transient gateway timeout / cold start / connection delay):
      // Retry the same endpoint with short backoff rather than failing over to trainova.co.in (which returns 404)
      const isSuspendedOrGatewayError = res.status === 502 || res.status === 503 || res.status === 504;
      if (!isRetry && isSuspendedOrGatewayError) {
        console.warn(`[Trainova API] Endpoint returned ${res.status} from ${url}. Retrying with backoff...`);
        await new Promise((resolve) => setTimeout(resolve, 800));
        return apiRequest<T>(path, method, body, params, true, forcedBaseUrl);
      }

      // If 401 Unauthorized, attempt transparent silent token refresh once
      if (
        res.status === 401 &&
        !isRetry &&
        !path.includes('/auth/login') &&
        !path.includes('/auth/refresh')
      ) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          // Re-execute with refreshed token
          return apiRequest<T>(path, method, body, params, true, forcedBaseUrl);
        } else {
          // Token is expired/invalid and cannot be refreshed — notify auth store
          clearStoredTokens();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('trainova:auth_expired'));
          }
        }
      }

    const text = await res.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }

    if (!res.ok) {
      if (json && typeof json === 'object' && json.error) {
        return json as ApiResponse<T>;
      }
      const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      );
      const customMsg = (res.status === 502 || res.status === 503 || res.status === 504)
        ? (isLocalhost
            ? '⚠️ Localhost Backend Offline: Cannot reach Express API on http://localhost:3001. Please run: npm run backend:dev'
            : (res.status === 503
                ? '⚠️ Backend Service Temporarily Unavailable (503): The Trainova server is currently busy or undergoing maintenance. Please retry in a moment.'
                : 'Backend service is currently starting or unavailable. Please retry in a few seconds.'))
        : (json?.message || `Request failed with status ${res.status}`);

      return {
        success: false,
        error: {
          code: `HTTP_${res.status}`,
          message: customMsg,
        },
      };
    }

    if (!json) {
      return {
        success: true,
        data: undefined as unknown as T,
      };
    }

    return json as ApiResponse<T>;
  } catch (err: unknown) {
    clearTimeout(timeoutId);
    const isLocalhost = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      /^192\.168\.|^10\.|^172\.(1[6-9]|2\d|3[0-1])\./.test(window.location.hostname)
    );

    const isNetworkOrConnError =
      err instanceof Error &&
      (err.name === 'TypeError' ||
        err.message.toLowerCase().includes('failed to fetch') ||
        err.message.toLowerCase().includes('networkerror') ||
        err.message.toLowerCase().includes('load failed'));

    // If network error occurred connecting to a relative or non-prod URL, failover to PROD_API_ORIGIN
    if (
      !isRetry &&
      isNetworkOrConnError &&
      !url.startsWith(PROD_API_ORIGIN) &&
      !isLocalhost
    ) {
      console.warn(`[Trainova API] Network error connecting to ${url}. Failing over directly to ${PROD_API_ORIGIN}...`);
      return apiRequest<T>(path, method, body, params, true, PROD_API_ORIGIN);
    }

    let message =
      err instanceof Error
        ? err.name === 'AbortError'
          ? 'Request timed out. Please try again.'
          : err.message
        : 'Failed to communicate with server.';

    if (isLocalhost && isNetworkOrConnError) {
      message = '⚠️ Localhost Backend Offline: Cannot connect to http://localhost:3001. Please make sure the backend is running (`npm run backend:dev` or `npm run dev:all`).';
    }

    return {
      success: false,
      error: {
        code: err instanceof Error && err.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK_ERROR',
        message,
      },
    };
  }
};

  const reqPromise = executeRequest();

  if (dedupeKey && !isRetry) {
    inFlightRequests.set(dedupeKey, reqPromise);
    reqPromise.finally(() => {
      inFlightRequests.delete(dedupeKey);
    });
  }

  return reqPromise;
}

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem(TOKEN_KEY) ||
    localStorage.getItem('token') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('trainova_token') ||
    null
  );
}

export function setStoredToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem('token', token);
  }
}

export function getStoredRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem(REFRESH_TOKEN_KEY) ||
    localStorage.getItem('refreshToken') ||
    null
  );
}

export function setStoredRefreshToken(refreshToken: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem('refreshToken', refreshToken);
  }
}

export function clearStoredTokens(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('trainova_token');
    localStorage.removeItem('refreshToken');
  }
}

export function clearStoredToken(): void {
  clearStoredTokens();
}

export function isAuthenticated(): boolean {
  return !!getStoredToken();
}

// ---------------------------------------------------------------------------
// API surface — mirrors TrainovaApiClient from apps/web/src/services/api.ts
// ---------------------------------------------------------------------------

// Auth
export const auth = {
  /** POST /api/v1/auth/login?tenant=<slug> */
  login: (credentials: { email: string; password: string; rememberMe?: boolean; tenantSlug?: string }) => {
    const { tenantSlug, ...body } = credentials;
    // tenant resolved server-side from ?tenant= query param (mirrors app.js L158)
    return apiRequest(
      '/api/v1/auth/login',
      'POST',
      body,
      tenantSlug ? { tenant: tenantSlug } : undefined
    );
  },

  /** GET /api/v1/auth/me — server-side JWT verification */
  me: () => apiRequest('/api/v1/auth/me'),

  logout: (refreshToken?: string) =>
    apiRequest('/api/v1/auth/logout', 'POST', { refreshToken }),

  refresh: (refreshToken: string) =>
    apiRequest('/api/v1/auth/refresh', 'POST', { refreshToken }),

  forgotPassword: (email: string, tenantSlug?: string) =>
    apiRequest(
      '/api/v1/auth/password/forgot',
      'POST',
      { email },
      tenantSlug ? { tenant: tenantSlug } : { tenant: 'acme-corp' }
    ),

  resetPassword: (data: { token: string; newPassword: string; tenantId: string }) =>
    apiRequest('/api/v1/auth/password/reset', 'POST', data),
};

// Tenant
export const tenant = {
  getPolicy: (tenantSlug: string) =>
    apiRequest('/api/v1/tenant/policy', 'GET', undefined, { tenant: tenantSlug }),
};

// Simulations / Scenarios
export const simulations = {
  list: (filter?: { status?: string; q?: string; search?: string; sortBy?: string; sortOrder?: string; page?: number; limit?: number }) =>
    apiRequest<any>('/api/v1/me/simulations', 'GET', undefined, filter as Record<string, any>),

  getBriefing: (simulationId: string) =>
    apiRequest(`/api/v1/simulations/${simulationId}/briefing`),

  submitConsent: (simulationId: string, data: { documentVersion: string; consentStatus?: string }) =>
    apiRequest(`/api/v1/simulations/${simulationId}/consent`, 'POST', data),

  startAttempt: (simulationId: string, data?: { language?: string; diagnostics?: unknown }) =>
    apiRequest(`/api/v1/simulations/${simulationId}/attempts`, 'POST', data),

  getAttemptHistory: (simulationId: string) =>
    apiRequest<{ simulation: any; attempts: any[]; totalAttempts: number }>(`/api/v1/simulations/${simulationId}/attempts`),
};

// Attempts
export const attempts = {
  list: (filter?: { status?: string; search?: string; page?: number; limit?: number }) =>
    apiRequest<{
      attempts: any[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      stats: {
        total: number;
        inProgress: number;
        completed: number;
        expired: number;
        abandoned: number;
      };
    }>('/api/v1/attempts', 'GET', undefined, filter as Record<string, any>),

  getDetails: (attemptId: string) =>
    apiRequest<{
      attempt: any;
      scenario: any;
      progress: any;
      quota: any;
      resumeEligibility: any;
    }>(`/api/v1/attempts/${attemptId}`),

  getState: (attemptId: string) =>
    apiRequest(`/api/v1/attempts/${attemptId}/state`),

  updateState: (attemptId: string, stateUpdates: unknown) =>
    apiRequest(`/api/v1/attempts/${attemptId}/state`, 'PATCH', stateUpdates),

  processTurn: (
    attemptId: string,
    learnerUtterance: string,
    speech?: {
      sttConfidence?: number;
      /**
       * Learner utterance capture as an audio data URL. Transit only — the server
       * stores a media URL for it, never the binary.
       */
      audioBase64?: string;
      interruption?: unknown;
      interruptions?: unknown;
      speechDurationMs?: number;
      hesitationMs?: number;
      wordCount?: number;
      speechStartMs?: number;
      speechEndMs?: number;
    }
  ) =>
    apiRequest(`/api/v1/attempts/${attemptId}/turn`, 'POST', {
      learnerUtterance,
      ...(speech || {}),
    }),

  submit: (attemptId: string, reason = 'LEARNER_CONFIRMED', turns?: any[], elapsedSeconds?: number) => {
    // Strip heavy base64 audio binaries from turns to keep transcript lightweight and robust
    const sanitizedTurns = Array.isArray(turns)
      ? turns.map((t) => {
          if (!t) return t;
          const { audioUrl, audioBase64, ...rest } = t;
          return rest;
        })
      : turns;
    return apiRequest(`/api/v1/attempts/${attemptId}/submit`, 'POST', { reason, turns: sanitizedTurns, elapsedSeconds });
  },

  resume: (attemptId: string, data?: { sessionToken?: string; forceTakeover?: boolean }) =>
    apiRequest(`/api/v1/attempts/${attemptId}/resume`, 'POST', data),

  heartbeat: (attemptId: string, sessionToken?: string) =>
    apiRequest(`/api/v1/attempts/${attemptId}/heartbeat`, 'POST', { sessionToken }),

  getResults: (attemptId: string, forceEval?: boolean) =>
    apiRequest(`/api/v1/attempts/${attemptId}/results${forceEval ? '?forceEval=true' : ''}`),

  getEvidence: (attemptId: string) =>
    apiRequest(`/api/v1/attempts/${attemptId}/evidence`),

  recordDiagnostics: (attemptId: string, telemetry: unknown) =>
    apiRequest('/api/v1/attempts/diagnostics', 'POST', { attemptId, telemetry }),

  synthesizeSpeech: (attemptId: string, data: { text: string; ttsVoice?: string; ttsProvider?: string; country?: string; tone?: string; speakingPace?: number; pitchPreference?: number; genderPreference?: string }) =>
    apiRequest<{ success: boolean; audioBase64?: string; mimeType?: string }>(`/api/v1/attempts/${attemptId}/synthesize-speech`, 'POST', data),
};

// Admin
export const admin = {
  // Tenants (Platform Admin)
  tenants: {
    // includeStats=false is for pickers: it skips the per-tenant count fan-out.
    list: (options?: { includeStats?: boolean }) =>
      apiRequest<{ tenants: any[]; total: number }>(
        '/api/v1/admin/tenants',
        'GET',
        undefined,
        options?.includeStats === false ? { stats: 'false' } : undefined
      ),
    get: (id: string) => apiRequest<any>(`/api/v1/admin/tenants/${id}`),
    create: (data: { name: string; slug: string; primaryColor?: string; logoUrl?: string; loginLogoUrl?: string | null; topNavLogoUrl?: string | null; directLoginUrl?: string | null; mobileNavConfig?: any; adminUser?: any }) =>
      apiRequest<any>('/api/v1/admin/tenants', 'POST', data),
    update: (id: string, data: { name?: string; primaryColor?: string; logoUrl?: string; loginLogoUrl?: string | null; topNavLogoUrl?: string | null; directLoginUrl?: string | null; learnerCanViewOwnResultReport?: boolean; videoAnalysisEnabled?: boolean; openAiVoiceEnabled?: boolean; openAiVoiceEntitled?: boolean; mobileNavConfig?: any; status?: string; isActive?: boolean }) =>
      apiRequest<any>(`/api/v1/admin/tenants/${id}`, 'PATCH', data),
    getSettings: (tenantId?: string) =>
      apiRequest<any>('/api/v1/admin/tenant/settings', 'GET', undefined, tenantId ? { tenantId } : undefined),
    updateSettings: (data: any, tenantId?: string) =>
      apiRequest<any>('/api/v1/admin/tenant/settings', 'PATCH', data, tenantId ? { tenantId } : undefined),
  },

  // Users (Tenant Admin)
  users: {
    list: (tenantId?: string) =>
      apiRequest<{ users: any[]; total: number }>('/api/v1/admin/tenant/users', 'GET', undefined, tenantId ? { tenantId } : undefined),
    create: (data: { email: string; password: string; firstName: string; lastName: string; role?: string; employeeId?: string; designation?: string; team?: string; region?: string; grade?: string; managerId?: string; managerName?: string; customFields?: Record<string, any>; metadata?: Record<string, any> }, tenantId?: string) =>
      apiRequest<any>('/api/v1/admin/tenant/users', 'POST', data, tenantId ? { tenantId } : undefined),
    bulkImport: (users: any[], tenantId?: string) =>
      apiRequest<{ total: number; createdCount: number; failedCount: number; created: any[]; errors: string[] }>(
        '/api/v1/admin/tenant/users/bulk-import',
        'POST',
        { users, tenantId },
        tenantId ? { tenantId } : undefined
      ),
    update: (id: string, data: { firstName?: string; lastName?: string; role?: string; employeeId?: string; designation?: string; team?: string; region?: string; grade?: string; managerId?: string; managerName?: string; customFields?: Record<string, any>; metadata?: Record<string, any>; isActive?: boolean }, tenantId?: string) =>
      apiRequest<any>(`/api/v1/admin/tenant/users/${id}`, 'PATCH', data, tenantId ? { tenantId } : undefined),
    updateRole: (id: string, role: string, tenantId?: string) =>
      apiRequest<any>(`/api/v1/admin/tenant/users/${id}/role`, 'PATCH', { role }, tenantId ? { tenantId } : undefined),
    updateStatus: (id: string, isActive: boolean, tenantId?: string) =>
      apiRequest<any>(`/api/v1/admin/tenant/users/${id}/status`, 'PATCH', { isActive }, tenantId ? { tenantId } : undefined),
    resetPassword: (id: string, data: { newPassword?: string; generateLinkOnly?: boolean }, tenantId?: string) =>
      apiRequest<any>(`/api/v1/admin/tenant/users/${id}/reset-password`, 'POST', data, tenantId ? { tenantId } : undefined),
  },

  // LLM Config
  getLlmConfig: (tenantId?: string) =>
    apiRequest('/api/v1/admin/llm-config', 'GET', undefined, tenantId ? { tenantId } : undefined),

  updateLlmConfig: (config: unknown, tenantId?: string) =>
    apiRequest('/api/v1/admin/llm-config', 'POST', config, tenantId ? { tenantId } : undefined),

  // Storage & Media Config
  getStorageConfig: (tenantId?: string) =>
    apiRequest('/api/v1/admin/storage-config', 'GET', undefined, tenantId ? { tenantId } : undefined),

  updateStorageConfig: (config: unknown, tenantId?: string) =>
    apiRequest('/api/v1/admin/storage-config', 'POST', config, tenantId ? { tenantId } : undefined),

  testS3Connection: (config: unknown) =>
    apiRequest('/api/v1/admin/test-s3-connection', 'POST', config),

  // Simulations / Cases
  listCases: (query?: Record<string, string | number | boolean>) =>
    apiRequest('/api/v1/admin/simulations', 'GET', undefined, query),

  createCase: (caseData: unknown) =>
    apiRequest('/api/v1/admin/simulations', 'POST', caseData),

  getCase: (caseId: string) =>
    apiRequest(`/api/v1/admin/simulations/${caseId}`),

  updateCase: (caseId: string, caseData: unknown) =>
    apiRequest(`/api/v1/admin/simulations/${caseId}`, 'PUT', caseData),

  deleteCase: (caseId: string) =>
    apiRequest(`/api/v1/admin/simulations/${caseId}`, 'DELETE'),

  publishCase: (caseId: string) =>
    apiRequest(`/api/v1/admin/simulations/${caseId}/publish`, 'POST'),

  // Audit Logs (Super Admin & Tenant Admins)
  getAuditLogs: (params?: {
    tenantId?: string;
    action?: string;
    targetType?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) =>
    apiRequest<{
      logs: any[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>('/api/v1/admin/audit-logs', 'GET', undefined, params as Record<string, any>),

  // Super Admin: Cross-tenant scenario availability whitelist
  getTenantAvailability: (simulationId: string) =>
    apiRequest<{
      simulationId: string;
      title: string;
      availableToTenantIds: string[];
      tenants: Array<{ id: string; name: string; slug: string; isActive: boolean }>;
    }>(`/api/v1/admin/simulations/${simulationId}/tenant-availability`),

  setTenantAvailability: (simulationId: string, tenantIds: string[]) =>
    apiRequest<any>(`/api/v1/admin/simulations/${simulationId}/tenant-availability`, 'PUT', { tenantIds }),

  // Tenant Admin: Bulk match file rows (Excel/CSV)
  bulkMatchAssignments: (simulationId: string, rows: Array<string | { raw?: string; employeeId?: string; email?: string }>, tenantId?: string) =>
    apiRequest<{
      summary: {
        total: number;
        willAssign: number;
        alreadyAssigned: number;
        inactive: number;
        notFound: number;
      };
      rows: Array<{
        row: number;
        fromFile: string;
        learnerName: string;
        team: string;
        designation: string;
        region: string;
        status: 'WILL_ASSIGN' | 'ALREADY_ASSIGNED' | 'INACTIVE' | 'NOT_FOUND';
        reason: string;
        userId: string | null;
      }>;
    }>(`/api/v1/admin/simulations/${simulationId}/assignments/bulk-match`, 'POST', { rows, tenantId }),

  // Tenant Admin: Bulk assign to learners
  bulkAssignSimulations: (
    simulationId: string,
    userIds: string[],
    options?: { maxAttempts?: number; startDate?: string | null; dueDate?: string | null; timeLimitMinutes?: number },
    tenantId?: string
  ) =>
    apiRequest<{
      count: number;
      assignments: any[];
    }>(`/api/v1/admin/simulations/${simulationId}/assignments/bulk-assign`, 'POST', {
      userIds,
      maxAttempts: options?.maxAttempts,
      startDate: options?.startDate,
      dueDate: options?.dueDate,
      timeLimitMinutes: options?.timeLimitMinutes,
      tenantId,
    }),

  // Comprehensive Simulation & Learner Reports
  getReports: (params?: {
    simulationId?: string;
    tenantId?: string;
    team?: string;
    designation?: string;
    region?: string;
    result?: string;
    search?: string;
  }) =>
    apiRequest<{
      isSuperAdmin: boolean;
      activeTenantId: string;
      simulations: Array<{ id: string; title: string; category: string; status: string }>;
      selectedSimulation: {
        id: string;
        title: string;
        category: string;
        passScore: number;
        timeLimitMinutes: number;
      } | null;
      competencies: Array<{
        key: string;
        name: string;
        weight: number;
        targetScore: number;
        avgScore: number;
      }>;
      globalMetrics: {
        assigned: number;
        completed: number;
        passed: number;
        notPassed: number;
        inProgress: number;
        notStarted: number;
        passRate: number;
        avgScore: number;
        avgDurationMinutes: number;
        distribution: { passed: number; notPassed: number; inProgress: number; notStarted: number };
      };
      metrics: {
        assigned: number;
        completed: number;
        passed: number;
        notPassed: number;
        inProgress: number;
        notStarted: number;
        passRate: number;
        avgScore: number;
        avgDurationMinutes: number;
        distribution: { passed: number; notPassed: number; inProgress: number; notStarted: number };
      };
      learners: Array<{
        userId: string;
        name: string;
        email: string;
        employeeId: string;
        team: string;
        designation: string;
        region: string;
        attemptsCount: number;
        maxAttempts: number;
        bestScore: number | null;
        competencyScores: Record<string, number | null>;
        result: 'Passed' | 'Not passed' | 'In progress' | 'Not started';
        lastAttemptDate: string;
        lastAttemptId: string | null;
      }>;
      tenantReports?: Array<{
        tenantId: string;
        tenantName: string;
        tenantSlug: string;
        metrics: {
          assigned: number;
          completed: number;
          passed: number;
          notPassed: number;
          inProgress: number;
          notStarted: number;
          passRate: number;
          avgScore: number;
          avgDurationMinutes: number;
          distribution: { passed: number; notPassed: number; inProgress: number; notStarted: number };
        };
        learners: Array<{
          userId: string;
          name: string;
          email: string;
          employeeId: string;
          team: string;
          designation: string;
          region: string;
          attemptsCount: number;
          maxAttempts: number;
          bestScore: number | null;
          competencyScores: Record<string, number | null>;
          result: 'Passed' | 'Not passed' | 'In progress' | 'Not started';
          lastAttemptDate: string;
          lastAttemptId: string | null;
        }>;
      }>;
    }>('/api/v1/admin/reports', 'GET', undefined, params as Record<string, any>),

  // Real-time LLM Execution & Token Observability Audit Trail
  getLlmAuditLogs: (params?: {
    tenantId?: string;
    purpose?: string;
    provider?: string;
    model?: string;
    status?: string;
    scenarioId?: string;
    attemptId?: string;
    search?: string;
    /** Inclusive date window. Date-only values cover the whole local calendar day. */
    startDate?: string;
    endDate?: string;
    /** Single calendar day shortcut (YYYY-MM-DD). */
    date?: string;
    actorId?: string;
    actorRole?: string;
    actorEmail?: string;
    page?: number;
    limit?: number;
  }) =>
    apiRequest<{
      logs: any[];
      summary: any;
      pagination: any;
    }>('/api/v1/admin/llm-audit', 'GET', undefined, params as Record<string, any>),
};


// Dashboard
export const dashboard = {
  /** GET /api/v1/dashboard — authenticated dashboard with simulations + stats */
  get: (params?: { page?: number; limit?: number; status?: string; q?: string; search?: string; sortBy?: string; sortOrder?: string }) =>
    apiRequest<any>('/api/v1/dashboard', 'GET', undefined, params as Record<string, any>),
};

// Health (Express backend)
export const health = {
  check: () => apiRequest('/health'),
};

// Scenarios / Case Studio (Phase 4)
export const scenarios = {
  list: (params?: {
    status?: string;
    category?: string;
    difficulty?: string;
    search?: string;
    tenantId?: string;
    page?: number;
    limit?: number;
  }) =>
    apiRequest<{
      scenarios: any[];
      total: number;
      page?: number;
      limit?: number;
      totalPages?: number;
      hasMore?: boolean;
    }>('/api/v1/scenarios', 'GET', undefined, params as Record<string, any>),

  get: (id: string, tenantId?: string) =>
    apiRequest<any>(`/api/v1/scenarios/${id}`, 'GET', undefined, tenantId ? { tenantId } : undefined),

  create: (data: any, tenantId?: string) =>
    apiRequest<any>('/api/v1/scenarios', 'POST', data, tenantId ? { tenantId } : undefined),

  update: (id: string, data: any, tenantId?: string) =>
    apiRequest<any>(`/api/v1/scenarios/${id}`, 'PATCH', data, tenantId ? { tenantId } : undefined),

  publish: (id: string, tenantId?: string) =>
    apiRequest<any>(`/api/v1/scenarios/${id}/publish`, 'POST', undefined, tenantId ? { tenantId } : undefined),

  unpublish: (id: string, tenantId?: string) =>
    apiRequest<any>(`/api/v1/scenarios/${id}/unpublish`, 'POST', undefined, tenantId ? { tenantId } : undefined),

  duplicate: (id: string, tenantId?: string) =>
    apiRequest<any>(`/api/v1/scenarios/${id}/duplicate`, 'POST', undefined, tenantId ? { tenantId } : undefined),

  delete: (id: string, tenantId?: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/v1/scenarios/${id}`, 'DELETE', undefined, tenantId ? { tenantId } : undefined),
};

// STT
export const stt = {
  transcribe: (audioBase64: string, language = 'en') =>
    apiRequest('/api/v1/stt/transcribe', 'POST', { audioBase64, language }),
};

// Evaluation Studio (Phase 27)
export const evaluationStudio = {
  listCases: () =>
    apiRequest<any[]>('/api/v1/evaluation-configs/cases'),
  getConfigById: (id: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/${id}`),
  getPublishedConfig: (simulationId: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/simulation/${simulationId}`),
  listConfigs: (simulationId: string) =>
    apiRequest<any[]>(`/api/v1/evaluation-configs/simulation/${simulationId}/all`),
  createDraft: (data: any) =>
    apiRequest<any>('/api/v1/evaluation-configs', 'POST', data),
  updateDraft: (id: string, data: any) =>
    apiRequest<any>(`/api/v1/evaluation-configs/${id}`, 'PUT', data),
  previewPrompt: (data: { template: string; context?: any; promptType?: string }) =>
    apiRequest<any>('/api/v1/evaluation-configs/preview', 'POST', data),
  validateConfig: (data: any) =>
    apiRequest<any>('/api/v1/evaluation-configs/validate', 'POST', data),
  testEvaluation: (data: { config: any; sampleContext?: any; promptType?: string }) =>
    apiRequest<any>('/api/v1/evaluation-configs/test', 'POST', data),
  runGoldenSet: (configId: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/${configId}/regression`, 'POST'),
  compareDiff: (data: { configIdA: string; configIdB: string; sampleTranscript?: any[] }) =>
    apiRequest<any>('/api/v1/evaluation-configs/diff', 'POST', data),
  approveConfig: (id: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/${id}/approve`, 'POST'),
  publishConfig: (id: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/${id}/publish`, 'POST'),
  rollbackConfig: (id: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/${id}/rollback`, 'POST'),
  getAuditTrail: (simulationId: string) =>
    apiRequest<any[]>(`/api/v1/evaluation-configs/simulation/${simulationId}/audit`),
  getQAMetrics: (simulationId: string) =>
    apiRequest<any>(`/api/v1/evaluation-configs/simulation/${simulationId}/qa-metrics`),
};

// Video Recording (Phase 11+)
export const video = {
  initRecording: (attemptId: string, payload?: { mimeType?: string; scenarioId?: string; expectedDurationSeconds?: number }) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/recording/init`, 'POST', payload),

  completeRecording: (attemptId: string, payload: { recordingId?: string; storageKey?: string; publicUrl?: string; fileSize?: number; durationSeconds?: number; metadata?: any }) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/recording/complete`, 'POST', payload),

  failRecording: (attemptId: string, errorReason: string) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/recording/fail`, 'POST', { errorReason }),

  getRecording: (attemptId: string) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/recording`, 'GET'),

  deleteRecording: (attemptId: string) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/recording`, 'DELETE'),
};

// Real-Time AI Assist (Phase 28)
export const realtimeAssist = {
  getState: (attemptId: string) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/assist/state`, 'GET'),
  getHistory: (attemptId: string) =>
    apiRequest<any[]>(`/api/v1/attempts/${attemptId}/assist/history`, 'GET'),
  recordAction: (attemptId: string, suggestionId: string, action: string) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/assist/action`, 'POST', { suggestionId, action }),
  trigger: (attemptId: string, payload: any) =>
    apiRequest<any>(`/api/v1/attempts/${attemptId}/assist/trigger`, 'POST', payload),
  getAudit: (attemptId: string) =>
    apiRequest<any[]>(`/api/v1/attempts/${attemptId}/assist/audit`, 'GET'),
  getMetrics: () =>
    apiRequest<any>('/api/v1/assist/metrics', 'GET'),
};

// Media Upload & Storage (Cloudinary + Local)
export const media = {
  upload: (data: { file: string; fileName?: string; mimeType?: string; resourceType?: 'image' | 'video' | 'audio' | 'auto'; folder?: string }) =>
    apiRequest<{ url: string; publicId: string; provider: 'cloudinary' | 'local'; format?: string; bytes?: number }>('/api/v1/media/upload', 'POST', data),
  getStatus: () =>
    apiRequest<{ provider: string; cloudinaryConfigured: boolean; cloudName?: string; folder: string }>('/api/v1/media/status'),
};

// Learner Custom Fields / UDF Studio (Prototype #set-udf)
export const customFields = {
  list: (tenantId?: string) =>
    apiRequest<{ fields: any[]; quota: { activeCount: number; maxLimit: number } }>(
      '/api/v1/admin/custom-fields',
      'GET',
      undefined,
      tenantId ? { tenantId } : undefined
    ),
  create: (data: any, tenantId?: string) =>
    apiRequest<any>(
      '/api/v1/admin/custom-fields',
      'POST',
      tenantId ? { ...data, tenantId } : data,
      tenantId ? { tenantId } : undefined
    ),
  update: (id: string, data: any, tenantId?: string) =>
    apiRequest<any>(
      `/api/v1/admin/custom-fields/${id}`,
      'PUT',
      tenantId ? { ...data, tenantId } : data,
      tenantId ? { tenantId } : undefined
    ),
  delete: (id: string, tenantId?: string) =>
    apiRequest<any>(
      `/api/v1/admin/custom-fields/${id}`,
      'DELETE',
      undefined,
      tenantId ? { tenantId } : undefined
    ),
};

// Multilingual & Translation System API
export const languages = {
  getAvailable: () => apiRequest<any[]>('/api/v1/languages/available'),
  getTenantLanguages: (params?: { tenant?: string }) =>
    apiRequest<{
      supportedLanguages: string[];
      defaultLanguage: string;
      fallbackLanguage: string;
      allowUserLanguageSelection: boolean;
      availableLanguages: any[];
    }>('/api/v1/languages', 'GET', undefined, params as any),
  translate: (data: { text?: string; texts?: string[]; sourceLang?: string; targetLang: string; context?: string; format?: 'text' | 'html' }) =>
    apiRequest<any>('/api/v1/translate', 'POST', data),
  getAdminConfig: (params?: { tenantId?: string }) =>
    apiRequest<any>('/api/v1/languages/admin/config', 'GET', undefined, params as any),
  updateAdminConfig: (data: any, params?: { tenantId?: string }) =>
    apiRequest<any>('/api/v1/languages/admin/config', 'PUT', data, params as any),
  testTranslation: (data: { text: string; targetLang: string }, params?: { tenantId?: string }) =>
    apiRequest<any>('/api/v1/languages/admin/test', 'POST', data, params as any),
  updateMyLanguage: (preferredLanguage: string) =>
    apiRequest<any>('/api/v1/auth/me/language', 'PATCH', { preferredLanguage }),
};

export const api = {
  get: <T = any>(path: string, params?: Record<string, string | number | boolean>) =>
    apiRequest<T>(path, 'GET', undefined, params),
  post: <T = any>(path: string, body?: unknown, params?: Record<string, string | number | boolean>) =>
    apiRequest<T>(path, 'POST', body, params),
  put: <T = any>(path: string, body?: unknown, params?: Record<string, string | number | boolean>) =>
    apiRequest<T>(path, 'PUT', body, params),
  patch: <T = any>(path: string, body?: unknown, params?: Record<string, string | number | boolean>) =>
    apiRequest<T>(path, 'PATCH', body, params),
  delete: <T = any>(path: string, params?: Record<string, string | number | boolean>) =>
    apiRequest<T>(path, 'DELETE', undefined, params),
  auth,
  tenant,
  simulations,
  scenarios,
  attempts,
  admin,
  dashboard,
  health,
  stt,
  video,
  media,
  evaluationStudio,
  realtimeAssist,
  customFields,
  languages,
};

export default api;


